import { createFileRoute, Link } from "@tanstack/react-router";
import { Callout } from "@/components/calv/Callout";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { SingleCase } from "@/components/calv/SingleCase";
import { EngagementGrid } from "@/components/calv/EngagementGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { DisciplineGrid } from "@/components/calv/DisciplineGrid";
import { relatedBySource } from "@/lib/related-services-data";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/")({
  head: () => {
    const h = pageHead("performanceMarketing", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(serviceSchema({
              name: "Performance Marketing",
              description: "Search-, Social-Media- und Affiliate-Performance über Google Ads, Meta, TikTok, LinkedIn und Microsoft Ads — als kontinuierliche, regelbasierte Systemsteuerung, operativ durch den Geschäftsführer.",
              url: "/leistungen/performance-marketing",
            })),
            jsonLdScript(breadcrumbSchema([
              { name: "Start", path: "/" },
              { name: "Leistungen", path: "/leistungen" },
              { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
            ])),
          ],
    };
  },
  component: PerformanceMarketingPage,
});

const steps = [
  {
    num: "01",
    title: "Audit & Datenfundament",
    body: "Konten-, Tracking- und Wettbewerbsanalyse. Datenlücken werden geschlossen, bevor irgendetwas anderes passiert.",
  },
  {
    num: "02",
    title: "Hypothesen-Setup",
    body: "Branchen- und kontenspezifische Hypothesen kalibrieren. Confidence-Schwellen und Eskalations-Pfade festlegen.",
  },
  {
    num: "03",
    title: "Live-Steuerung",
    body: "Tägliches Monitoring, regelbasierte Auto-Anpassungen unter der Schwelle, Senior-Review bei Anomalien — der GF sitzt selbst am Konto.",
  },
  {
    num: "04",
    title: "Wöchentlicher Review",
    body: "Ergebnis-Review, Hypothesen-Verifikation, Roadmap-Anpassung. Vier Augen mindestens, jede Aktion rückverfolgbar.",
  },
];

const capabilities = [
  { name: "Google Ads — Search", detail: "Brand · Non-Brand · Long-Tail" },
  { name: "Google Ads — Shopping & PMax", detail: "Feed-Hygiene · Asset-Group-Disziplin" },
  { name: "YouTube & Demand Gen", detail: "Awareness · View-Through (in Google Ads)" },
  { name: "Meta Ads", detail: "Facebook · Instagram · Advantage+" },
  { name: "TikTok Ads", detail: "Spark Ads · Creator-Sourcing" },
  { name: "LinkedIn Ads", detail: "B2B · ABM · Conversation Ads" },
  { name: "Microsoft Ads", detail: "Search · Audience Network" },
  { name: "Affiliate-Marketing", detail: "Partner-Steuerung · Tracking" },
  { name: "Server-side Tracking", detail: "Enhanced Conversions · GA4" },
  { name: "Negativlisten-Hygiene", detail: "Regelbasiert · täglich" },
];

const stack = [
  { name: "Google Ads", small: "Search · Shopping · PMax · YouTube" },
  { name: "Meta Ads Manager", small: "Facebook · Instagram" },
  { name: "TikTok Ads", small: "incl. Events API" },
  { name: "LinkedIn Ads", small: "B2B-Targeting" },
  { name: "Microsoft Ads", small: "Search · UET" },
  { name: "Google Analytics 4", small: "+ BigQuery Export" },
  { name: "Google Tag Manager", small: "server & web" },
  { name: "Delta", small: "Feed-Management · proprietär" },
];

const engagements = [
  {
    label: "Mediabudget",
    title: "Ab 3.500 € pro Monat",
    body: "Das ist die Schwelle, ab der Engine-Optimierung sich rechnet. Darunter ist der Hebel zu klein — wir empfehlen dann ehrlich andere Wege.",
  },
  {
    label: "Vorlauf",
    title: "Vier bis sechs Wochen",
    body: "Audit, Tracking-Setup und Engine-Kalibrierung brauchen Zeit. Versprechen, die kürzer sind, halten wir nicht.",
  },
  {
    label: "Mindestlaufzeit",
    title: "Vier Quartale",
    body: "Erst nach drei Monaten zeigen systemische Optimierungen Compound-Effekte. Vier Quartale geben den Hebeln Zeit, voll zu wirken.",
  },
  {
    label: "Honorarmodell",
    title: "Effizienzanteil + Grundgebühr",
    body: (
      <>
        Festes Senior-Honorar plus Beteiligung an den messbaren Effizienzgewinnen —{" "}
        <strong className="text-ink">nicht</strong> am Werbebudget. So sind unsere
        Anreize gleichgerichtet.
      </>
    ),
  },
];

function PerformanceMarketingPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing"
          headline={
            <>
              Performance Marketing als <em>Steuerungssystem</em>, nicht als Ausgabenposten.
            </>
          }
          lead="Wir betreiben SEA, Shopping und Paid Social über Google Ads, Meta, TikTok, LinkedIn und Microsoft Ads — als ein integriertes Steuerungssystem mit gemeinsamer Tracking-Substanz, Pacing-Disziplin und Senior-Review. Geeignet für Unternehmen, die ihr Performance-Marketing aus der Black Box holen wollen."
          metaStrip={[
            "10–200 K€ monatliche Ad-Budgets",
            "Senior-Review",
            "Server-Side-Tracking als Standard",
            "GF-operativ",
          ]}
          primaryCta={{
            eyebrow: "Kostenfrei · circa 30 Minuten",
            label: "Strategiegespräch anfragen",
            to: "/kontakt",
          }}
          secondaryCta={{
            label: "Oder: Performance-Marketing-Checkliste ohne Anmeldung herunterladen",
            to: "/tools/performance-marketing-checkliste",
          }}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing" },
          ]}
        />

        <Section variant="compact">
          <Callout>
            <strong>Im Zusammenhang:</strong> Performance Marketing arbeitet kanalübergreifend mit einheitlicher Methodik. Wer speziell die integrierte Social-Methodik sucht — Organic, Paid und Community als gemeinsames System auf Meta, TikTok, LinkedIn und Pinterest — findet sie unter <Link to="/leistungen/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
        </Section>


        <Section
          number="02"
          kicker="Standpunkt"
          headline={
            <>
              Was wir an Performance Marketing <em>anders machen.</em>
            </>
          }
        >
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">
                Die meisten Mediabudgets fließen in Konten, in die „ein- bis zweimal
                pro Woche jemand reinschaut". Detailbewegungen — Wettbewerbs-Drift,
                Search-Term-Anomalien, tageszeitliche Conversion-Verschiebungen —
                bleiben unerkannt. Die Folge: hohe Klickkosten ohne entsprechende
                Hebel.
              </p>
              <p className="mb-5">
                Wir verstehen Performance Marketing als{" "}
                <strong className="font-semibold text-ink">
                  kontinuierliche Systemsteuerung
                </strong>
                . Konto-Hygiene, Wettbewerbs-Drift und Search-Term-Anomalien
                werden täglich geprüft, regelbasierte Anpassungen laufen
                automatisiert, Anomalien gehen direkt an den Geschäftsführer —
                ohne Hand-off. Damit verlagert sich der menschliche Beitrag
                dorthin, wo er Wert schafft: in Strategie und
                Ausnahmeentscheidungen, nicht in Routinepflege.
              </p>
              <p>
                <strong className="font-semibold text-ink">
                  Preisgestaltung dazu:
                </strong>{" "}
                Wir partizipieren grundsätzlich <em>nicht</em> am Mediabudget — diese Kopplung
                schafft Anreize, die nicht zu Ihren passen. Wenn Sie das wünschen, kann eine
                Komponente unseres Honorars an die messbar erzeugten Einsparungen gekoppelt
                werden. Damit haben wir denselben Anreiz wie Sie: möglichst viel Wirkung pro Euro.
              </p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="03"
          kicker="Performance-Marketing im Detail"
          headline={<>Sieben Disziplinen, <em>sieben Detailseiten.</em></>}
          caption="Jeder Performance-Kanal funktioniert nach eigener Logik. Wer wirtschaftlich steuern will, braucht kanal-spezifische Tiefe — nicht generische Aufzählungen. Hier finden Sie die einzelnen Disziplinen mit ihrer Calvarius-Methodik."
        >
          <DisciplineGrid
            items={[
              { eyebrow: "SEARCH · SHOPPING · PMAX", title: "Google Ads", subtitle: "Wirtschaftlich gesteuert, nicht plattformhörig", href: "/leistungen/performance-marketing/google-ads" },
              { eyebrow: "FACEBOOK · INSTAGRAM", title: "Meta Ads", subtitle: "Performance ohne Plattformlogik, mit Creative-Disziplin", href: "/leistungen/performance-marketing/meta-ads" },
              { eyebrow: "B2B · ABM · LEAD GEN", title: "LinkedIn Ads", subtitle: "B2B-Performance mit Pipeline-Disziplin", href: "/leistungen/performance-marketing/linkedin-ads" },
              { eyebrow: "SPARK ADS · CREATOR", title: "TikTok Ads", subtitle: "Visual-Performance mit Spark-Ad-Disziplin", href: "/leistungen/performance-marketing/tiktok-ads" },
              { eyebrow: "BING · LINKEDIN-PROFIL", title: "Microsoft Ads", subtitle: "Search-Komplement für DACH", href: "/leistungen/performance-marketing/microsoft-ads" },
              { eyebrow: "AWARENESS · VIDEO", title: "YouTube & Demand Gen", subtitle: "Awareness mit Performance-Logik", href: "/leistungen/performance-marketing/youtube-demand-gen" },
              { eyebrow: "PARTNER · TRACKING", title: "Affiliate-Marketing", subtitle: "Partner-Steuerung mit Tracking-Disziplin", href: "/leistungen/performance-marketing/affiliate-marketing" },
            ]}
          />
        </Section>

        <Section
          variant="tinted"
          number="04"
          kicker="Vorgehen"
          headline={<>Vier Phasen, die <em>nie wirklich enden.</em></>}
          caption="Die Zusammenarbeit ist kein Projekt mit Anfang und Ende, sondern eine kontinuierliche Schleife. Phase eins läuft einmalig, die Phasen zwei bis vier rotieren wöchentlich."
        >
          <ProcessFlow steps={steps} />
        </Section>

        <Section
          number="05"
          kicker="Capabilities & Stack"
          headline={<>Was konkret in jeder Zusammenarbeit <em>steuerbar ist.</em></>}
          caption="Jedes Element unten ist ein Hebel, den wir in laufenden Kundenprojekten bewegen. Nicht alles passt zu jedem Kunden — aber alles steht zur Verfügung."
        >
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
          <div className="mt-12 rounded-lg border border-rule bg-bg-subtle p-7">
            <p className="eyebrow mb-4">Eingesetzte eigene Tools</p>
            <div className="flex flex-wrap gap-3">
              <a href="/leistungen/delta" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">Delta — Feed-Management →</a>
              <a href="/leistungen/picazzoo" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">picazzoo — Creative-Produktion →</a>
              <a href="/leistungen/gonzalads" className="rounded-md border border-rule bg-white px-4 py-2 font-mono text-xs uppercase tracking-wider text-ink-2 hover:border-cyan hover:text-cyan-deep">GonzalAds — Meta-Kampagnen →</a>
            </div>
          </div>
        </Section>

        <Section
          variant="tinted"
          number="06"
          kicker="Muster"
          headline={<>Was möglich wird, wenn man <em>aufhört zu raten.</em></>}
          caption="Eine typische Konstellation aus unserer B2B- und SaaS-Praxis — anonymisiert beschrieben."
        >
          <SingleCase
            meta="Muster · B2B · SaaS"
            title="Volumen wächst, qualifizierte Pipeline nicht."
            body={
              <>
                <p>
                  Ausgangslage: hohe Werbeausgaben in Google Ads, aber unklarer
                  Beitrag zur Pipeline. CRM und Ad-Konto laufen getrennt,
                  Lead-Qualität bleibt im Reporting unsichtbar.
                </p>
                <p>
                  Eingriff: Neuaufsatz des serverseitigen Trackings,
                  Lead-Scoring-Integration direkt in Google Ads, gezielte
                  Content-Investitionen in Bottom-Funnel-Cluster — über
                  CRM-Daten täglich rebalanciert.
                </p>
              </>
            }
            statValue="GF"
            statLabel={<>operativ am Konto<br />statt Hand-off an Juniors</>}
          />
        </Section>

        <LogoReferences locale="de" number="07" />

        <RelatedServices
          locale="de"
          fromId="performance-marketing"
          number="08"
          items={relatedBySource["performance-marketing"]}
        />

        <Section
          number="09"
          kicker="Engagement"
          headline={<>Was Performance Marketing <em>realistisch braucht.</em></>}
        >
          <EngagementGrid items={engagements} />
          <div className="mt-12">
            <CTABlock
              headline={<>Ist Ihr <em>Performance-Budget</em> wirtschaftlich gesteuert?</>}
              body="Sie möchten wissen, welche Teile Ihres Performance-Budgets wirklich arbeiten – und welche nicht?"
              primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
              secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
            />
          </div>
        </Section>
        <GlossaryTerms locale="de" ids={["performance-marketing", "cpa", "cpc", "attribution-model"]} />
      </main>
      <Footer />
    </>
  );
}
